/**
 * Screen Orientation Helper
 *
 * Provides a unified interface for managing screen orientation changes and lock states.
 * Combines native Capacitor events with custom event management for comprehensive
 * orientation handling across the application.
 *
 * Features:
 * - Single native listener with custom event distribution
 * - Custom emitter for orientation changes
 * - Comprehensive debug logging
 * - TypeScript support with proper typing
 * - Automatic event firing on programmatic orientation changes
 */

import {
  ScreenOrientation,
  OrientationType,
  type LockOptions,
} from '@capawesome/capacitor-screen-orientation'
import { Capacitor } from '@capacitor/core'
import { SafeArea } from 'capacitor-plugin-safe-area'

// Re-export types and enums for convenience so consumers don't need to import from the plugin directly
export { OrientationType }
export type { LockOptions }

/**
 * Generic listener handle type compatible with Capacitor's listener system.
 * Provides a remove() method to clean up event listeners.
 */
type ListenerHandle = {
  remove: () => Promise<void>
}

/**
 * Common data structure for all orientation-related events.
 * Used consistently across both native and custom events.
 */
type OrientationEventData = {
  /** The current or target screen orientation */
  orientation: OrientationType
}

/**
 * Custom event emitter for orientation-related events.
 *
 * Provides a simple event system that stores callbacks and can emit events
 * to all registered listeners. Used for screen orientation changes.
 *
 * @example
 * ```typescript
 * const emitter = new OrientationEventEmitter()
 * const handle = await emitter.addListener((data) => {
 *   console.log('Orientation event:', data)
 * })
 *
 * emitter.emit({ orientation: OrientationType.LANDSCAPE })
 * await handle.remove() // Clean up listener
 * ```
 */
class OrientationEventEmitter {
  /** Array of registered callback functions */
  private listeners: ((data: OrientationEventData) => void)[] = []

  /**
   * Adds a new event listener.
   *
   * @param callback - Function to call when events are emitted
   * @returns Promise resolving to a handle for removing the listener
   */
  addListener(
    callback: (data: OrientationEventData) => void,
  ): Promise<ListenerHandle> {
    console.debug(
      '[ScreenOrientation] Adding listener, total listeners before:',
      this.listeners.length,
    )
    this.listeners.push(callback)
    console.debug(
      '[ScreenOrientation] Added listener, total listeners now:',
      this.listeners.length,
    )

    return Promise.resolve({
      remove: () =>
        Promise.resolve().then(() => {
          const callbacks = this.listeners
          if (callbacks) {
            const index = callbacks.indexOf(callback)
            if (index > -1) {
              console.debug(
                '[ScreenOrientation] Removing listener, total listeners before:',
                callbacks.length,
              )
              callbacks.splice(index, 1)
              console.debug(
                '[ScreenOrientation] Removed listener, total listeners now:',
                callbacks.length,
              )
            } else {
              console.warn(
                '[ScreenOrientation] Attempted to remove listener that was not found',
              )
            }
          }
        }),
    })
  }

  /**
   * Emits an event to all registered listeners.
   *
   * @param data - The event data to send to all listeners
   */
  emit(data: OrientationEventData) {
    console.debug(
      '[ScreenOrientation] Emitting event to',
      this.listeners.length,
      'listeners:',
      data,
    )
    const callbacks = this.listeners
    if (callbacks) {
      callbacks.forEach((callback, index) => {
        console.debug(
          `[ScreenOrientation] Calling listener ${index + 1}/${callbacks.length}`,
        )
        callback(data)
      })
    } else {
      console.debug('[ScreenOrientation] No listeners to emit to')
    }
  }
}

/** Event emitter for screen orientation change events (native + manual) */
const screenOrientationChangeEmitter = new OrientationEventEmitter()

/** Global handle for the single native orientation change listener */
let nativeListenerHandle: ListenerHandle | null = null

/**
 * Initializes the native orientation change listener if not already created.
 * This ensures only one native listener exists regardless of how many custom
 * listeners are registered.
 *
 * The native listener transforms Capacitor events into our unified format
 * and distributes them through the custom emitter system.
 *
 * @private
 */
const initializeNativeListener = async () => {
  if (!nativeListenerHandle) {
    console.debug(
      '[ScreenOrientation] Initializing native listener for screenOrientationChange',
    )
    nativeListenerHandle = await ScreenOrientation.addListener(
      'screenOrientationChange',
      (data) => {
        console.debug(
          '[ScreenOrientation] Native orientation change event received:',
          data,
        )
        // Transform and emit through our custom emitter to all listeners
        const eventData = {
          orientation: data.type,
        }
        console.debug(
          '[ScreenOrientation] Transforming and emitting native event:',
          eventData,
        )

        // Capacitor safe area plugin did'nt allowed us to choose a better solution
        void (async () => {
          const getComparableSafeAreaState = async () => {
            const { insets } = await SafeArea.getSafeAreaInsets()
            return [insets.top, insets.bottom, insets.left, insets.right].join(
              ',',
            )
          }

          const before = await getComparableSafeAreaState()

          const timeouts: number[] = []
          const clearTimeouts = () => {
            for (const timeout of timeouts) {
              clearTimeout(timeout)
            }
          }

          console.debug(
            '[ScreenOrientation] Directly emitting screenOrientationChange event',
          )
          screenOrientationChangeEmitter.emit(eventData)

          // Check for safe area changes for 950ms at 50ms interval
          for (let i = 0; i < 20; i++) {
            const timeout: number = setTimeout(async () => {
              const current = await getComparableSafeAreaState()
              if (current !== before) {
                setTimeout(() => {
                  screenOrientationChangeEmitter.emit(eventData)
                }, 20) // delay event emit 20ms
                console.debug(
                  '[ScreenOrientation] Safe area insets changed, firing screenOrientationChange event a second time',
                  {
                    before,
                    current,
                  },
                )
                clearTimeouts()
              }
            }, i * 50) as unknown as number // vite thinks result of setTimeout is not assignable to number
            timeouts.push(timeout)
          }

          // Fallback timeout at 1000ms that gets active when state change wasnt detected and timeouts array wasnt cleared yet
          const fallbackTimeout: number = setTimeout(() => {
            // Fallback emit
            screenOrientationChangeEmitter.emit(eventData)
            console.debug(
              '[ScreenOrientation] Safe area insets didnt changed, firing fallback screenOrientationChange event',
            )
          }, 1000) as unknown as number
          timeouts.push(fallbackTimeout)
        })()
      },
    )
    console.debug(
      '[ScreenOrientation] Native listener initialized successfully',
    )
  } else {
    console.debug(
      '[ScreenOrientation] Native listener already exists, skipping initialization',
    )
  }
}

/**
 * Adds a listener for screen orientation events.
 *
 * Listens for 'screenOrientationChange' events that are fired when device
 * orientation changes (native or programmatic).
 *
 * The function automatically handles native listener initialization and provides
 * a unified event data format.
 *
 * @param callback - Function to call when the event occurs
 * @returns Promise resolving to a handle for removing the listener
 *
 * @example
 * ```typescript
 * // Listen for orientation changes
 * const handle = await addScreenOrientationChangeListener(
 *   (data) => console.log('Orientation:', data.orientation)
 * )
 *
 * // Clean up listener
 * await handle.remove()
 * ```
 */
export function addScreenOrientationChangeListener(
  callback: (data: OrientationEventData) => void,
): Promise<ListenerHandle> {
  console.debug('[ScreenOrientation] Adding listener for orientation changes')

  console.debug('[ScreenOrientation] Initializing native listener if needed')
  // Initialize native listener on first call
  void initializeNativeListener()

  // Add to our custom emitter for unified handling
  return screenOrientationChangeEmitter.addListener(callback)
}

/**
 * Gets the current screen orientation from the native platform.
 *
 * @returns Promise resolving to the current orientation information
 * @example
 * ```typescript
 * const orientation = await getCurrentScreenOrientation()
 * console.log('Current orientation:', orientation.type)
 * ```
 */
export const getCurrentScreenOrientation = async () => {
  return await ScreenOrientation.getCurrentOrientation()
}

/**
 * Locks the screen orientation to a specific orientation.
 *
 * Only works on native platforms (iOS/Android). On web platforms, this function
 * does nothing but logs the attempt.
 *
 * When successful, this function:
 * 1. Locks the screen to the specified orientation
 * 2. Fires a 'screenOrientationChange' event if the orientation actually changed
 *
 * @param options - Lock options specifying the target orientation
 * @returns Promise that resolves when the lock operation completes
 *
 * @example
 * ```typescript
 * import { OrientationType } from '@/helpers/screenOrientation'
 *
 * // Lock to landscape
 * await lockScreenOrientation({ type: OrientationType.LANDSCAPE })
 *
 * // Lock to portrait
 * await lockScreenOrientation({ type: OrientationType.PORTRAIT })
 * ```
 */
export const lockScreenOrientation = async (
  options: LockOptions,
): Promise<void> => {
  console.debug(
    '[ScreenOrientation] lockScreenOrientation called with options:',
    options,
  )

  if (Capacitor.isNativePlatform()) {
    try {
      console.debug('[ScreenOrientation] Attempting to lock screen orientation')
      await ScreenOrientation.lock(options)
    } catch (error) {
      console.error(
        '[ScreenOrientation] Error locking screen orientation:',
        error,
      )
    }
  } else {
    console.debug(
      '[ScreenOrientation] Not on native platform, skipping lock operation',
    )
  }
}

/**
 * Unlocks the screen orientation, allowing free rotation.
 *
 * This function:
 * 1. Unlocks the screen orientation
 * 2. Fires a 'screenOrientationChange' event if the orientation changed after unlock
 *
 * @returns Promise that resolves when the unlock operation completes
 *
 * @example
 * ```typescript
 * // Unlock orientation to allow free rotation
 * await unlockScreenOrientation()
 * ```
 */
export const unlockScreenOrientation = async () => {
  if (Capacitor.isNativePlatform()) {
    await ScreenOrientation.unlock()
    console.debug(
      '[ScreenOrientation] Successfully unlocked screen orientation',
    )
  } else {
    console.debug(
      '[ScreenOrientation] Not on native platform, skipping unlock operation',
    )
  }
}
